/**
 * DeleteConfirmationDialog - Unified delete confirmation popover
 *
 * Displays a positioned popover overlay near the delete button with Delete and Cancel buttons.
 * Automatically positions itself relative to the trigger element.
 * Used across requirement, product, action, thread, and graph node deletion flows.
 *
 * @example
 * ```tsx
 * const [deleteAnchor, setDeleteAnchor] = useState<HTMLElement | null>(null);
 *
 * <Button onClick={(e) => setDeleteAnchor(e.currentTarget)}>
 *   <Trash2 />
 * </Button>
 *
 * <DeleteConfirmationDialog
 *   anchor={deleteAnchor}
 *   itemType="product"
 *   itemName={product.name}
 *   onConfirm={handleDelete}
 *   onCancel={() => setDeleteAnchor(null)}
 * />
 * ```
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button';
import { toRefusal, type Refusal } from '@sourcepilot/ui/framework/data/refusal';
import { RefusalNotice, type RefusalAction } from '@sourcepilot/ui/framework/ui/molecules/RefusalNotice';

interface DeleteConfirmationDialogProps {
  /** The element to anchor the popover to (takes precedence over position) */
  anchor?: HTMLElement | null;
  /** Direct position for the dialog (used if anchor is not provided) */
  position?: { x: number; y: number } | null;
  /** Type of item being deleted (e.g., "product", "action", "requirement") */
  itemType?: string;
  /** Optional item name to include in confirmation message */
  itemName?: string;
  /** Called when user confirms deletion. A throw (a refused delete) keeps
   *  the dialog open with the server's account of why (VC-D R1:
   *  `island_in_use` names the projects that pin it) */
  onConfirm: () => void | Promise<void>;
  /** the refusal's next steps this caller can do (`retire` → "Retire
   *  instead"); one that completes closes the dialog, one that throws shows
   *  its own refusal */
  refusalActions?: Record<string, { label: string; run: () => void | Promise<void>; title?: string } | undefined>;
  /** Called when user cancels */
  onCancel: () => void;
  /** Custom message (overrides default) */
  message?: string;
  /** Additional className for container */
  className?: string;
}

export function DeleteConfirmationDialog({
  anchor,
  position,
  itemType = 'item',
  itemName,
  onConfirm,
  onCancel,
  message,
  className = '',
  refusalActions = {},
}: DeleteConfirmationDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [working, setWorking] = useState(false);

  // Generate confirmation message
  const confirmationMessage = message || (
    itemName
      ? `Delete "${itemName}"?`
      : `Delete this ${itemType}?`
  );

  const isVisible = !!(anchor || position);
  // a closed dialog forgets its refusal
  useEffect(() => { if (!isVisible) { setRefusal(null); setWorking(false); } }, [isVisible]);

  // Close on click outside
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && e.target instanceof Node && !dialogRef.current.contains(e.target)) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible, onCancel]);

  // Close on Escape key
  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, onCancel]);

  const handleConfirm = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setRefusal(null); setWorking(true);
    try {
      await onConfirm();
    } catch (err) {
      setWorking(false);
      setRefusal(toRefusal(err, 'It was not deleted.'));
      return; // stays open: the refusal says why, and what can be done instead
    }
    setWorking(false);
    onCancel(); // Close after confirm
  };
  const offered: Record<string, RefusalAction | undefined> = {};
  for (const [name, a] of Object.entries(refusalActions)) {
    if (!a) continue;
    offered[name] = {
      label: a.label, title: a.title, disabled: working,
      run: () => {
        setWorking(true);
        void Promise.resolve().then(a.run).then(
          () => { setWorking(false); onCancel(); },
          (err) => { setWorking(false); setRefusal(toRefusal(err, 'That did not work.')); },
        );
      },
    };
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCancel();
  };

  if (!isVisible) return null;

  // Calculate position — flip to the left side of the anchor if a
  // right-aligned popover would overflow the viewport.
  const DIALOG_WIDTH = 300; // matches maxWidth below
  const MARGIN = 8;
  let left: number, top: number;
  if (anchor) {
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : DIALOG_WIDTH * 2;
    const preferredLeft = rect.right + MARGIN;
    if (preferredLeft + DIALOG_WIDTH + MARGIN > viewportWidth) {
      // Not enough room on the right — anchor to the left of the button
      left = Math.max(MARGIN, rect.left - DIALOG_WIDTH - MARGIN);
    } else {
      left = preferredLeft;
    }
    top = rect.top;
  } else if (position) {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : DIALOG_WIDTH * 2;
    const preferredLeft = position.x + MARGIN;
    if (preferredLeft + DIALOG_WIDTH + MARGIN > viewportWidth) {
      left = Math.max(MARGIN, position.x - DIALOG_WIDTH - MARGIN);
    } else {
      left = preferredLeft;
    }
    top = position.y;
  } else {
    return null;
  }

  return (
    <div
      ref={dialogRef}
      className={`fixed z-50 bg-white border border-red-300 rounded-lg shadow-xl p-3 ${className}`}
      style={{
        left: `${left}px`,
        top: `${top}px`,
        minWidth: '200px',
        maxWidth: '300px'
      }}
    >
      <div className="text-sm text-gray-800 font-medium mb-3">{confirmationMessage}</div>
      {refusal && <div className="mb-3"><RefusalNotice refusal={refusal} actions={offered} testId="delete-refusal" /></div>}
      <div className="flex gap-2 justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="px-3 py-1 text-xs"
        >
          Cancel
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleConfirm}
          disabled={working}
          data-testid="delete-confirm"
          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs"
        >
          Delete
        </Button>
      </div>
    </div>
  );
}
